import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CalendarPlus, Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { buildWhatsAppLink, TEMPLATES, formatAppointmentDateTime } from "../lib/whatsapp";

/**
 * Prompt the user to book a follow-up appointment for the patient.
 * Shown right after a prescription is saved.
 *
 * Props:
 *   - open: boolean
 *   - onOpenChange: fn
 *   - patient: { patient_id, name, phone }
 *   - doctorId: string (current doctor's user_id)
 *   - clinicName: string
 */
function addDays(d, days) {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

function isoDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const PRESETS = [
  { label: "+3 days", days: 3 },
  { label: "+7 days", days: 7 },
  { label: "+14 days", days: 14 },
  { label: "+30 days", days: 30 },
];

export default function FollowUpDialog({ open, onOpenChange, patient, doctorId, clinicName }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("10:00");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [resolvedDoctorId, setResolvedDoctorId] = useState(doctorId || "");

  useEffect(() => {
    if (open) {
      setDate(isoDate(addDays(new Date(), 7)));
      setTime("10:00");
      setError("");
      if (doctorId) {
        setResolvedDoctorId(doctorId);
      } else {
        // Fetch first available doctor for clinics without a default
        api.get("/doctors").then(({ data }) => {
          if (data?.length) setResolvedDoctorId(data[0].user_id);
        }).catch(() => {});
      }
    }
  }, [open, doctorId]);

  const pickPreset = (days) => {
    setDate(isoDate(addDays(new Date(), days)));
  };

  const handleBook = async () => {
    setError("");
    if (!date || !time) { setError("Pick date and time"); return; }
    if (!patient?.patient_id) { setError("Missing patient"); return; }
    if (!resolvedDoctorId) { setError("No doctor available"); return; }
    setSubmitting(true);
    try {
      const dt = new Date(`${date}T${time}:00`);
      await api.post("/appointments", {
        patient_id: patient.patient_id,
        doctor_id: resolvedDoctorId,
        date_time: dt.toISOString(),
        duration_minutes: 30,
        reason: "Follow-up",
      });
      toast.success("Follow-up booked");
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not book follow-up");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookAndShare = async () => {
    setError("");
    if (!date || !time) { setError("Pick date and time"); return; }
    if (!resolvedDoctorId) { setError("No doctor available"); return; }
    setSubmitting(true);
    try {
      const dt = new Date(`${date}T${time}:00`);
      await api.post("/appointments", {
        patient_id: patient.patient_id,
        doctor_id: resolvedDoctorId,
        date_time: dt.toISOString(),
        duration_minutes: 30,
        reason: "Follow-up",
      });
      toast.success("Follow-up booked");
      const fmt = formatAppointmentDateTime(dt.toISOString());
      const msg = TEMPLATES.appointmentReminder({
        patient_name: patient?.name,
        clinic_name: clinicName,
        appointment_date: fmt.date,
        appointment_time: fmt.time,
      });
      const link = buildWhatsAppLink(patient?.phone, msg);
      window.open(link, "_blank", "noopener,noreferrer");
      onOpenChange(false);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not book follow-up");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="followup-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
            <CalendarPlus className="w-5 h-5 text-blue-600" /> Book a follow-up?
          </DialogTitle>
          <DialogDescription>
            Schedule {patient?.name ? patient.name.split(" ")[0] : "the patient"}'s follow-up visit.
            You can skip if not needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Quick pick
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p.days}
                  type="button"
                  onClick={() => pickPreset(p.days)}
                  data-testid={`followup-preset-${p.days}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    date === isoDate(addDays(new Date(), p.days))
                      ? "bg-blue-600 border-blue-600 text-white"
                      : "bg-white border-slate-200 text-slate-700 hover:border-blue-400"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date</Label>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                data-testid="followup-date-input"
                className="mt-1.5 h-10"
              />
            </div>
            <div>
              <Label className="text-xs">Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="followup-time-input"
                className="mt-1.5 h-10"
              />
            </div>
          </div>
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="followup-error">
              {error}
            </div>
          )}
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="followup-skip-btn">
            Skip
          </Button>
          <Button
            variant="outline"
            onClick={handleBook}
            disabled={submitting}
            data-testid="followup-book-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book"}
          </Button>
          <Button
            onClick={handleBookAndShare}
            disabled={submitting || !patient?.phone}
            className="bg-green-600 hover:bg-green-700"
            data-testid="followup-book-whatsapp-btn"
            title={!patient?.phone ? "Patient phone missing" : ""}
          >
            <MessageCircle className="w-4 h-4 mr-1.5" /> Book & WhatsApp
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
